import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, Star, MessageCircle, Handshake, MapPin, Trophy, SlidersHorizontal } from "lucide-react";

const ESTADOS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

export default function PesquisarMontadoras() {
  const [filtros, setFiltros] = useState({
    estado: "", cidade: "", bairro: "", nicho: "",
    tipo_servico: "", capacidade_min: "", score_min: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [favoritas, setFavoritas] = useState([]);
  const [catalogo, setCatalogo] = useState({ nichos: [], servicos_por_nicho: {} });
  const [parceriaOpen, setParceriaOpen] = useState(null);
  const [scoreOpen, setScoreOpen] = useState(null);
  const [casosOpen, setCasosOpen] = useState(null);
  const [casos, setCasos] = useState([]);

  const verCasos = async (m) => {
    setCasosOpen(m);
    try {
      const r = await api.get(`/casos-sucesso?montadora_id=${m.id}`);
      setCasos(r.data);
    } catch (e) { setCasos([]); }
  };

  useEffect(() => {
    api.get("/catalogo/nichos").then(r => setCatalogo(r.data)).catch(() => {});
    api.get("/empresa/favoritas").then(r => setFavoritas(r.data || [])).catch(() => {});
  }, []);

  const buscar = useCallback(() => {
    setLoading(true);
    const params = Object.entries(filtros).filter(([_, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/empresa/pesquisar-montadoras${params ? "?" + params : ""}`)
      .then(r => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filtros]);

  useEffect(() => { buscar(); }, []);  // eslint-disable-line

  const toggleFav = async (mid) => {
    try {
      const r = await api.post(`/empresa/favoritar-montadora/${mid}`);
      setFavoritas((prev) =>
        r.data.favoritado ? [...prev, mid] : prev.filter((x) => x !== mid)
      );
      toast.success(r.data.favoritado ? "Adicionada aos favoritos" : "Removida dos favoritos");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const servicosDisponiveis = filtros.nicho
    ? (catalogo.servicos_por_nicho[filtros.nicho] || [])
    : Object.values(catalogo.servicos_por_nicho || {}).flat();
  const servicosUnicos = [...new Set(servicosDisponiveis)];

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">MontaJá · Rede</div>
        <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Pesquisar Montadoras</h1>
        <p className="text-sm text-aj-text-muted mt-1">
          Encontre a montadora ideal para sua operação por região, nicho e score (11 parâmetros).
        </p>
      </div>

      {/* Filtros */}
      <div className="aj-card p-5">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-aj-blue-dark mb-4">
          <SlidersHorizontal size={14} className="text-aj-orange" /> Filtros de busca
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Estado</Label>
            <Select value={filtros.estado} onValueChange={(v) => setFiltros(f => ({...f, estado: v === "_todos" ? "" : v}))}>
              <SelectTrigger className="mt-2 rounded-lg" data-testid="pm-estado"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_todos">Todos</SelectItem>
                {ESTADOS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FF label="Cidade" v={filtros.cidade} on={(v) => setFiltros(f => ({...f, cidade: v}))} testid="pm-cidade" />
          <FF label="Bairro/região" v={filtros.bairro} on={(v) => setFiltros(f => ({...f, bairro: v}))} testid="pm-bairro" />
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Nicho</Label>
            <Select value={filtros.nicho} onValueChange={(v) => setFiltros(f => ({...f, nicho: v === "_todos" ? "" : v, tipo_servico: ""}))}>
              <SelectTrigger className="mt-2 rounded-lg" data-testid="pm-nicho"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_todos">Todos</SelectItem>
                {(catalogo.nichos || []).map(n => <SelectItem key={n} value={n} className="capitalize">{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Tipo de serviço</Label>
            <Select value={filtros.tipo_servico} onValueChange={(v) => setFiltros(f => ({...f, tipo_servico: v === "_todos" ? "" : v}))}>
              <SelectTrigger className="mt-2 rounded-lg" data-testid="pm-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_todos">Todos</SelectItem>
                {servicosUnicos.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <FF label="Capacidade mensal mínima" v={filtros.capacidade_min} on={(v) => setFiltros(f => ({...f, capacidade_min: v}))} testid="pm-cap" type="number" />
          <FF label="Score mínimo (0-100)" v={filtros.score_min} on={(v) => setFiltros(f => ({...f, score_min: v}))} testid="pm-score" type="number" />
          <div className="flex items-end">
            <button onClick={buscar} className="btn-aj-primary w-full justify-center text-sm" data-testid="pm-buscar">
              <Search size={16} /> Buscar
            </button>
          </div>
        </div>
      </div>

      {/* Resultados */}
      <div className="flex justify-between items-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
          {loading ? "Buscando…" : `${data?.total ?? 0} montadora(s) encontrada(s)`}
        </div>
        {favoritas.length > 0 && (
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-aj-text-muted">
            <Star size={13} className="text-aj-amber fill-aj-amber" /> {favoritas.length} favorita(s)
          </div>
        )}
      </div>

      {data?.resultados?.length === 0 ? (
        <div className="aj-card border-dashed p-16 text-center text-aj-text-muted">
          Nenhuma montadora encontrada com esses filtros.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {(data?.resultados || []).map(m => (
            <MontadoraCard key={m.id} m={m}
              favorita={favoritas.includes(m.id)}
              onToggleFav={() => toggleFav(m.id)}
              onSolicitarParceria={() => setParceriaOpen(m)}
              onVerScore={() => setScoreOpen(m)}
              onVerCasos={() => verCasos(m)} />
          ))}
        </div>
      )}

      {/* Dialog Score breakdown */}
      <Dialog open={!!scoreOpen} onOpenChange={() => setScoreOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Score detalhado · {scoreOpen?.nome_fantasia || scoreOpen?.nome}</DialogTitle>
            <DialogDescription>Breakdown dos 11 parâmetros — últimos 90 dias.</DialogDescription>
          </DialogHeader>
          {scoreOpen && <ScoreBreakdown breakdown={scoreOpen.score_breakdown} score={scoreOpen.score} />}
        </DialogContent>
      </Dialog>

      {/* Dialog Solicitar parceria */}
      <Dialog open={!!parceriaOpen} onOpenChange={() => setParceriaOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Solicitar parceria</DialogTitle>
            <DialogDescription>
              Envie uma solicitação para a montadora <strong>{parceriaOpen?.nome_fantasia || parceriaOpen?.nome}</strong>.
              A comunicação será registrada e auditada.
            </DialogDescription>
          </DialogHeader>
          {parceriaOpen && (
            <ParceriaForm montadora={parceriaOpen} onDone={() => { setParceriaOpen(null); toast.success("Solicitação enviada"); }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Casos de Sucesso */}
      <Dialog open={!!casosOpen} onOpenChange={() => setCasosOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Trophy size={18} className="text-aj-orange" /> Casos de Sucesso · {casosOpen?.nome_fantasia || casosOpen?.nome}
            </DialogTitle>
            <DialogDescription>Trabalhos com avaliação 4–5★ publicados por esta montadora.</DialogDescription>
          </DialogHeader>
          {casos.length === 0 ? (
            <div className="p-8 text-center text-sm text-aj-text-muted">
              Esta montadora ainda não publicou casos de sucesso na plataforma.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
              {casos.map(c => {
                const API = process.env.REACT_APP_BACKEND_URL;
                return (
                  <div key={c.id} className="aj-card overflow-hidden" data-testid={`pesquisa-caso-${c.id}`}>
                    <div className="aspect-[4/3] bg-aj-surface-2 overflow-hidden">
                      <img src={`${API}/api${c.foto_url.replace("/api","")}`} alt={c.titulo}
                        className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-3 text-xs space-y-1">
                      <div className="flex gap-0.5">
                        {Array.from({ length: c.nota }).map((_, i) => (
                          <Star key={i} size={12} className="text-aj-amber fill-aj-amber" />
                        ))}
                      </div>
                      <div className="font-bold text-aj-text">{c.titulo}</div>
                      <div className="italic text-aj-text-muted">"{c.depoimento}"</div>
                      <div className="text-[10px] text-aj-text-muted pt-1 border-t border-[#E2E8F0]">
                        {c.cliente_nome} {c.cidade && `· ${c.cidade}/${c.estado}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MontadoraCard({ m, favorita, onToggleFav, onSolicitarParceria, onVerScore, onVerCasos }) {
  const score = m.score || 0;
  const corScore = score >= 80 ? "text-aj-success" : score >= 60 ? "text-aj-text" : "text-aj-danger";
  return (
    <div className="aj-card p-5" data-testid={`montadora-card-${m.id}`}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-display font-extrabold text-lg leading-tight text-aj-blue-deep">
            {m.nome_fantasia || m.razao_social || m.nome}
          </div>
          {(m.cidade || (m.area_atuacao || []).length > 0) && (
            <div className="flex items-center gap-1 text-xs text-aj-text-muted mt-1">
              {m.cidade && (
                <>
                  <MapPin size={12} className="text-aj-orange" />
                  {m.cidade}/{m.estado || ""} ·
                </>
              )}
              <span className="capitalize">{(m.area_atuacao || []).slice(0, 3).join(", ")}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            {m.status_operacional && m.status_operacional !== "ativa" && (
              <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold uppercase ${
                m.status_operacional === "nova" ? "bg-[#DBEAFE] text-[#142766]"
                : m.status_operacional === "em_observacao" ? "bg-aj-amber-soft text-aj-orange-hover"
                : "bg-[#FEE2E2] text-[#B91C1C]"}`}
                data-testid={`status-op-${m.id}`}>
                {m.status_operacional.replace("_", " ")}
              </span>
            )}
            {m.experiencia && (
              <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase bg-[#F4F4F5] text-aj-text"
                data-testid={`experiencia-${m.id}`}>{m.experiencia}</span>
            )}
          </div>
          {m.selo && (
            <div className="text-[11px] text-[#142766] mt-1 italic" data-testid={`selo-${m.id}`}>{m.selo}</div>
          )}
        </div>
        <button onClick={onToggleFav}
          className={`p-1.5 rounded-lg transition-colors ${favorita ? "bg-aj-amber-soft" : "hover:bg-aj-surface-2"}`}
          data-testid={`fav-${m.id}`}>
          <Star size={20} className={favorita ? "text-aj-amber fill-aj-amber" : "text-[#CBD5E1]"} />
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <button onClick={onVerScore} className="text-left group" data-testid={`score-${m.id}`}>
          <div className="text-[9px] font-bold uppercase tracking-wider text-aj-text-muted">Score</div>
          <div className={`font-display font-extrabold text-3xl group-hover:underline ${corScore}`}>{score.toFixed(1)}</div>
        </button>
        <div className="border-l border-[#E2E8F0] pl-3 flex-1">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Metric label="Concluídos" v={m.atendimentos_concluidos} />
            <button onClick={onVerCasos} className="text-left hover:underline" data-testid={`ver-casos-${m.id}`}>
              <div className="text-[9px] font-bold uppercase tracking-wider text-aj-text-muted">Casos sucesso</div>
              <div className="font-mono font-bold text-aj-orange inline-flex items-center gap-1">
                <Trophy size={11} /> {m.casos_sucesso ?? 0}
              </div>
            </button>
            <Metric label="Capac./mês" v={m.capacidade_mensal_estimada} />
            <Metric label="Nota média" v={m.nota_media_cliente?.toFixed(1) || "—"} />
          </div>
        </div>
      </div>

      <MiniBar breakdown={m.score_breakdown} />

      <div className="mt-4 flex gap-2">
        <Link to={`/admin/chat?montadora_id=${m.id}`} className="flex-1" data-testid={`chat-${m.id}`}>
          <Button variant="outline" className="w-full rounded-lg text-xs border-aj-blue-dark text-aj-blue-dark hover:bg-aj-blue-dark hover:text-white">
            <MessageCircle size={14} /> Conversar pelo chat
          </Button>
        </Link>
        <button onClick={onSolicitarParceria}
          className="flex-1 btn-aj-primary justify-center text-xs !py-2"
          data-testid={`parceria-${m.id}`}>
          <Handshake size={14} /> Solicitar parceria
        </button>
      </div>
    </div>
  );
}

function Metric({ label, v }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</div>
      <div className="font-mono font-bold text-aj-text">{v ?? 0}</div>
    </div>
  );
}

function MiniBar({ breakdown }) {
  if (!breakdown) return null;
  const top = ["taxa_conclusao", "satisfacao_cliente", "sla_30d"];
  return (
    <div className="grid grid-cols-3 gap-2 mt-3">
      {top.map(k => (
        <div key={k}>
          <div className="text-[9px] font-bold uppercase tracking-wider text-aj-text-muted">{k.replace(/_/g, " ")}</div>
          <div className="h-1.5 rounded-full bg-aj-surface-2 mt-1 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-aj-blue-dark to-aj-blue" style={{ width: `${breakdown[k] || 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ScoreBreakdown({ breakdown, score }) {
  const labels = {
    tempo_resposta: "Tempo médio de resposta",
    taxa_aceite: "Taxa de aceite",
    taxa_conclusao: "Taxa de conclusão",
    indice_reclamacao: "Índice de reclamação (invertido)",
    indice_atraso: "Índice de atraso (invertido)",
    sla_30d: "Cumprimento de SLA",
    satisfacao_cliente: "Satisfação do cliente",
    qualidade_comunicacao: "Qualidade da comunicação",
    capacidade_regional: "Capacidade regional",
    casos_sucesso: "Casos de sucesso",
    indice_problemas: "Índice de problemas (invertido)",
  };
  return (
    <div>
      <div className="text-center mb-4">
        <div className="font-display font-extrabold text-5xl text-aj-blue-deep">{score.toFixed(1)}</div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange mt-1">Score final ponderado</div>
      </div>
      <div className="space-y-2">
        {Object.entries(breakdown || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between items-center text-xs gap-3" data-testid={`brk-${k}`}>
            <div className="flex-1 text-aj-text">{labels[k] || k}</div>
            <div className="w-32 h-1.5 rounded-full bg-aj-surface-2 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-aj-orange to-aj-amber" style={{ width: `${v}%` }} />
            </div>
            <div className="font-mono w-10 text-right text-aj-text">{v.toFixed(0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParceriaForm({ montadora, onDone }) {
  const [mensagem, setMensagem] = useState("");
  const [volume, setVolume] = useState("");
  const [cidades, setCidades] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/empresa/solicitar-parceria", {
        montadora_id: montadora.id,
        mensagem,
        volume_estimado: volume ? parseInt(volume, 10) : null,
        cidades_alvo: cidades.split(",").map(s => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <FF label="Volume estimado/mês" v={volume} on={setVolume} testid="parc-volume" type="number" />
      <FF label="Cidades-alvo (vírgula)" v={cidades} on={setCidades} testid="parc-cidades" />
      <div>
        <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Mensagem</Label>
        <Textarea rows={4} value={mensagem} onChange={(e) => setMensagem(e.target.value)}
          className="mt-2 rounded-lg" placeholder="Conte um pouco sobre sua operação…"
          data-testid="parc-msg" />
      </div>
      <button onClick={submit} disabled={busy} className="btn-aj-primary w-full justify-center disabled:opacity-60"
        data-testid="parc-enviar">
        {busy ? "Enviando…" : "Enviar solicitação"}
      </button>
    </div>
  );
}

function FF({ label, v, on, testid, type="text" }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2 rounded-lg" data-testid={testid} />
    </div>
  );
}
